import { retrieveUserV3 } from '@alexis/helpers/authentications';
import React, { useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useOutletContext } from 'react-router-dom';

import Button from '@components/Button';
import Dialog from '@components/Dialog';
import Toast from '@components/Toast/Toast';

import { datadogLogger } from '@helpers/datadogLogger';

import NoData from '@icons/no_data_found.svg';

import { useDeleteTraveller, useGetTravellers } from '@hooks/useTravellersApi';

import { changeUser } from '@redux/actions/userActions';
import { changeUserV3 } from '@redux/actions/userV3Actions';
import {
  getDomainBasedApiBaseUrlState,
  getTranslationsState,
  getUserState,
} from '@redux/selectors';

import { Traveller } from '@wegoTypes/traveller';
import { User } from '@wegoTypes/user';
import { UserV3 } from '@wegoTypes/userV3';

import Skeleton from './components/Skeleton/Skeleton';
import TravellerCard from './components/TravellerCard/TravellerCard';
import TravellerFormView from './components/TravellerFormView/TravellerFormView';
import useTravellersTrackingActions from './hooks/useTravellersTrackingActions';
import styles from './Travellers.module.scss';

const Travellers = () => {
  const user: User | null = useSelector(getUserState);
  const { pageViewId } = useOutletContext<{ pageViewId: string }>();
  const [toastMessage, setToastMessage] = useState<string | null>(null);
  const [showRemoveDialog, setShowRemoveDialog] = useState<boolean>(false);
  const [showFormView, setShowFormView] = useState<boolean>(false);
  const [editTraveller, setEditTraveller] = useState<Traveller | undefined>();
  const [removeTraveller, setRemoveTraveller] = useState<Traveller | undefined>();
  const [isDeleting, setIsDeleting] = useState<boolean>(false);
  const getTravellerListQuery = useGetTravellers(true);
  const { data: travellerList, isLoading } = getTravellerListQuery;
  const { mutateAsync: deleteTraveller } = useDeleteTraveller();
  const translations = useSelector(getTranslationsState);

  const dispatch = useDispatch();
  const domainBasedApiBaseUrl: string = useSelector(getDomainBasedApiBaseUrlState);

  const { trackClickAddNewTraveller, trackClickRemoveTraveller } =
    useTravellersTrackingActions(pageViewId);

  const [primaryTraveller, additionalTravellers] = useMemo(() => {
    let primaryTraveller: Traveller | undefined;
    let additionalTravellers: Traveller[] = [];
    if (travellerList) {
      primaryTraveller = travellerList?.list.find((traveller) => traveller.primary);
      additionalTravellers = travellerList?.list.filter((traveller) => !traveller.primary) || [];
    }
    return [primaryTraveller, additionalTravellers];
  }, [travellerList]);

  const onClickEditTraveller = (traveller?: Traveller) => {
    setEditTraveller(traveller);
    setShowFormView(true);
  };

  const onClickAddNewTraveller = () => {
    trackClickAddNewTraveller();
    setEditTraveller(undefined);
    setShowFormView(true);
  };

  const onClickRemoveTraveller = (traveller: Traveller) => {
    setRemoveTraveller(traveller);
    setShowRemoveDialog(true);
  };

  const onCloseForm = (refreshUser?: boolean) => {
    if (refreshUser) {
      refreshUserDetails();
    }
    setEditTraveller(undefined);
    setShowFormView(false);
  };

  const refreshUserDetails = async () => {
    const userV3: UserV3 = await retrieveUserV3(domainBasedApiBaseUrl);
    dispatch(changeUserV3(userV3));
    dispatch(
      changeUser({
        ...user,
        displayName: !!userV3.profile.name?.trim() ? userV3.profile.name : userV3.profile.email,
        firstName: userV3.profile.firstName || user?.firstName,
        lastName: userV3.profile.lastName || user?.lastName,
        nationality: userV3.profile.nationality || user?.nationality,
        userHash: userV3.profile.userHash,
      } as User),
    );
  };

  const displayMessage = (message: string) => {
    setToastMessage(message);
  };

  const handleDeleteTraveller = async () => {
    if (!removeTraveller) return;
    trackClickRemoveTraveller();
    try {
      setIsDeleting(true);
      await deleteTraveller(removeTraveller?.id);
      displayMessage(translations.traveller_deleted);
    } catch (error) {
      displayMessage(translations.something_wrong_try_again);
      datadogLogger(error as Error, {
        description: 'Traveller Deletion Failed',
        errorSource: 'Travellers',
        states: {
          traveller: removeTraveller,
        },
      });
    } finally {
      setIsDeleting(false);
      setShowRemoveDialog(false);
      setRemoveTraveller(undefined);
    }
  };

  return isLoading ? (
    <div data-testid='travellers-skeleton' className={styles.loadingContainer}>
      <Skeleton />
    </div>
  ) : (
    <>
      {showFormView ? (
        <TravellerFormView
          pageViewId={pageViewId}
          displayMessage={displayMessage}
          traveller={editTraveller}
          onClose={onCloseForm}
        />
      ) : (
        <div className={styles.container}>
          {!primaryTraveller ? (
            <NoDataFound text={translations.no_traveller_description} />
          ) : (
            <>
              <div className={styles.heading}>{translations.main_traveller}</div>
              <TravellerCard traveller={primaryTraveller} />
              <div className={styles.divider}></div>
              {additionalTravellers.length > 0 && (
                <>
                  <div className={styles.heading}>{translations.other_travellers}</div>
                  <div className={styles.additionalTravellers}>
                    {additionalTravellers.map((traveler) => (
                      <TravellerCard
                        key={traveler.id}
                        traveller={traveler}
                        onEdit={() => onClickEditTraveller(traveler)}
                        onRemove={() => onClickRemoveTraveller(traveler)}
                      />
                    ))}
                  </div>
                </>
              )}
            </>
          )}

          <div role='button' className={styles.addTravellerBtn} onClick={onClickAddNewTraveller}>
            <div className={styles.btnText}>{translations.add_new_traveller}</div>
          </div>
          <Dialog isOpen={showRemoveDialog} onClose={() => setShowRemoveDialog(false)}>
            <div className={styles.dialog}>
              <div className={styles.heading}>{translations.traveller_delete_confirmation}</div>
              <div className={styles.description}>{translations.traveller_delete_warning}</div>
              <div className={styles.actions}>
                <Button
                  className={styles.actionBtn}
                  colorScheme='grey'
                  size='md'
                  variant='outline'
                  onClick={() => setShowRemoveDialog(false)}
                >
                  {translations.cancel}
                </Button>
                <Button
                  disabled={isDeleting}
                  className={styles.actionBtn}
                  size='md'
                  onClick={handleDeleteTraveller}
                >
                  {translations.delete}
                </Button>
              </div>
            </div>
          </Dialog>
        </div>
      )}
      <Toast message={toastMessage} setMessage={setToastMessage} />
    </>
  );
};

const NoDataFound = ({ text }: { text: string }) => {
  return (
    <div className={styles.noDataContainer}>
      <NoData />
      <div className={styles.title}>{text}</div>
    </div>
  );
};

export default Travellers;
